import { useState, useEffect, type FormEvent } from 'react'
import { Button } from './Button'
import { Spinner } from './Spinner'
import { useAuthStore } from '@/lib/store/auth-store'

interface CodexAuthDialogProps {
  onClose: () => void
}

export function CodexAuthDialog({ onClose }: CodexAuthDialogProps) {
  const [token, setToken] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [alreadyConnected, setAlreadyConnected] = useState(false)
  const userId = useAuthStore((s) => s.userId)

  useEffect(() => {
    fetch('/api/codex/status')
      .then((r) => r.json())
      .then((d: { authenticated: boolean }) => setAlreadyConnected(d.authenticated))
      .catch(() => {})
  }, [])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!token.trim()) return

    setSaving(true)
    setError(null)
    try {
      const res = await fetch('/api/codex/set-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: token.trim(), user: userId }),
      })
      const data = await res.json() as { ok?: boolean; error?: string }
      if (!res.ok || data.error) throw new Error(data.error ?? 'Failed')
      setAlreadyConnected(true)
      setToken('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save token')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-md bg-[rgb(var(--np-surface))] border border-[rgb(var(--np-border))] rounded-lg p-6 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-[rgb(var(--np-tx-primary))] mb-1">AI Features</h2>
        <p className="text-xs text-[rgb(var(--np-tx-muted))] mb-4">
          Connect an OpenAI API key or Codex access token for AI-powered location detection.
        </p>

        {alreadyConnected && (
          <div className="border-l-2 border-l-[rgb(var(--np-status-ok))] bg-[rgb(var(--np-status-ok)/0.05)] px-4 py-2 rounded-r mb-4">
            <p className="text-xs text-[rgb(var(--np-status-ok))] font-semibold">Connected</p>
            <p className="text-xs text-[rgb(var(--np-tx-muted))] mt-0.5">AI features are active. You can update the token below.</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="label-section mb-2 block">API Key or Access Token</label>
            <input
              type="password"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="sk-... or codex access token"
              className="w-full bg-[rgb(var(--np-raised))] border border-[rgb(var(--np-border))] rounded-md px-3 py-2.5 text-sm font-data text-[rgb(var(--np-tx-primary))] placeholder-[rgb(var(--np-tx-muted))] focus:outline-none focus:ring-1 focus:ring-[rgb(var(--np-accent)/0.5)] transition-all"
              disabled={saving}
            />
          </div>

          <p className="text-[10px] text-[rgb(var(--np-tx-muted))] leading-relaxed">
            Get an API key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-[rgb(var(--np-accent))] hover:underline">platform.openai.com</a>,
            or a Codex access token from your <a href="https://chatgpt.com/admin/access-tokens" target="_blank" rel="noopener noreferrer" className="text-[rgb(var(--np-accent))] hover:underline">ChatGPT admin console</a>.
            The token is stored in server memory only — not persisted to disk.
          </p>

          {error && (
            <div className="border-l-2 border-l-[rgb(var(--np-status-critical))] bg-[rgb(var(--np-status-critical)/0.05)] px-4 py-2 rounded-r">
              <p className="text-xs text-[rgb(var(--np-status-critical))]">{error}</p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" type="button" onClick={onClose}>
              {alreadyConnected ? 'Done' : 'Cancel'}
            </Button>
            <Button type="submit" disabled={saving || !token.trim()}>
              {saving ? <Spinner size={14} /> : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
